// The order's buyer across their orders (the order's Client tab): how many they placed, how many
// came back or had money back — all of it or a part — and what they spent (their lifetime value);
// and every one of their orders, searched by number or product, in a dialog of its own. The orders
// are the reader's: a seller sees the buyer's orders with them (aldoo-na/store/admin-orders clientOf).
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import { describeLine } from "aldoo-ra/Store/lines"
import { ExclamationTriangleIcon, ListBulletIcon } from "@heroicons/react/24/outline"
import { Chip, Empty, Thumb, money, dateOnly, timeAgo, orderState, placedAt } from "./ui"

/** Amounts in one currency or several (`[{ currency, amount }]`), as one figure. */
const amounts = (rows, currency) => (rows?.length ? rows.map((row) => money(row.amount, row.currency)).join(" + ") : money(0, currency))

/** The buyer returned goods or had money back: the red chip beside their name, which opens the Client tab. */
export const ClientFlag = ({ client, t, onClick }) => {
  if (!client?.flagged) return null
  const count = client.returned + client.partlyReturned + client.refunded + client.partlyRefunded
  return (
    <button
      type="button"
      onClick={onClick}
      // the icon and the count in the chip; the words in its tooltip
      title={t("%s returns/refunds", count)}
      aria-label={t("%s returns/refunds", count)}
      className="print:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums whitespace-nowrap bg-admin_error/10 text-admin_error hover:bg-admin_error/20"
      data-testid="order-client-flag"
    >
      <ExclamationTriangleIcon className="w-3.5 h-3.5" aria-hidden />
      {count}
    </button>
  )
}

const Tile = ({ label, value, hint, alert = false, testId }) => (
  <div className={`rounded-lg border px-4 py-3 flex flex-col justify-between gap-1 ${alert ? "border-admin_error/30 bg-admin_error/5" : "border-black/10"}`} data-testid={testId}>
    <div className={`text-xs ${alert ? "text-admin_error" : "text-black/50"}`}>{label}</div>
    <div className={`text-2xl font-semibold tabular-nums ${alert ? "text-admin_error" : "text-black"}`}>{value}</div>
    {hint && <div className="text-xs text-black/50">{hint}</div>}
  </div>
)

/** The order's Client tab: the buyer's figures, and the way to all of their orders. */
export default function ClientPanel({ order, api, t, locale, onOpenOrder }) {
  const [listing, setListing] = useState(false)
  const client = order?.client
  if (!client) return <Empty>{t`Nothing is known of this customer yet`}</Empty>
  const customer = order.customer || {}
  const address = order.address || order.shippingAddress || order.billingAddress || {}
  return (
    <div className="flex flex-col gap-4 py-4 text-sm" data-testid="order-client">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {/* the name, the email under it; no name: the email alone, once */}
          <div className="text-base font-semibold text-black">{customer.name || address.name || customer.email || "—"}</div>
          {(customer.name || address.name) && customer.email && <div className="text-black/60">{customer.email}</div>}
          {client.firstAt && (
            <div className="text-black/50 text-xs mt-1">
              {t("First order %s", dateOnly(client.firstAt, locale))}
              {client.lastAt && client.lastAt !== client.firstAt ? ` · ${t("last %s", timeAgo(client.lastAt, t))}` : ""}
            </div>
          )}
        </div>
        {api.clientOrders && (
          <Button variant="admin_secondary" onClick={() => setListing(true)} data-testid="order-client-all">
            <ListBulletIcon className="w-4 h-4 mr-1.5 inline" aria-hidden />
            {t`See all orders by this customer`}
          </Button>
        )}
      </div>

      {client.flagged && (
        <div className="flex items-start gap-2 rounded-lg border border-admin_error/30 bg-admin_error/5 p-3 text-black/80" data-testid="order-client-warning">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-admin_error" aria-hidden />
          {t`This customer has returned goods or had money back on their orders. Check the order before you send it.`}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-3">
        <Tile label={t`Orders`} value={client.orders} hint={client.canceled ? t("%s canceled", client.canceled) : null} testId="order-client-orders" />
        <Tile label={t`Returned`} value={client.returned} alert={client.returned > 0} testId="order-client-returned" />
        <Tile label={t`Partly returned`} value={client.partlyReturned} alert={client.partlyReturned > 0} testId="order-client-partly-returned" />
        <Tile label={t`Refunded`} value={client.refunded} alert={client.refunded > 0} testId="order-client-refunded" />
        <Tile label={t`Partly refunded`} value={client.partlyRefunded} alert={client.partlyRefunded > 0} testId="order-client-partly-refunded" />
        {/* what they paid, less what went back */}
        <Tile
          label={t`Lifetime value`}
          value={amounts(client.ltv, order.currency)}
          hint={client.refundedAmount?.length ? t("%s given back", amounts(client.refundedAmount)) : null}
          testId="order-client-ltv"
        />
      </div>

      {listing && (
        <ClientOrdersDialog
          order={order}
          api={api}
          t={t}
          locale={locale}
          onClose={() => setListing(false)}
          onOpenOrder={
            onOpenOrder &&
            ((id) => {
              setListing(false)
              onOpenOrder(id)
            })
          }
        />
      )}
    </div>
  )
}

/** The table's search object as the term the API takes. */
const searchTermOf = (search) => {
  if (!search) return ""
  if (typeof search === "string") return search
  const values = Object.values(search).flatMap((value) => (Array.isArray(value) ? value.flatMap((entry) => Object.values(entry)) : [value]))
  return values.find((value) => typeof value === "string" && value.trim()) || ""
}

/** Every order of the buyer, newest first: searched by its number or a product's name, each with what it came to. */
function ClientOrdersDialog({ order, api, t, locale, onClose, onOpenOrder }) {
  const id = order.orderId || order._id
  const name = order.customer?.name || (order.address || order.shippingAddress || order.billingAddress || {}).name || order.customer?.email || order.email
  const loadPage = async ({ page, itemsPerPage, search }) => {
    const res = await api.clientOrders(id, { page, items_per_page: itemsPerPage, search: searchTermOf(search) })
    return { data: res?.data || [], total: res?.total ?? 0 }
  }
  return (
    <Modal isOpen onClose={onClose} variant="admin_compact_fit" title={t("Orders by %s", name || "—")}>
      <div className="px-3 w-[min(960px,92vw)]" data-testid="client-orders">
        <DataTable
          variant="admin"
          apiMethod={loadPage}
          apiDataField="data"
          itemIdField="_id"
          itemsPerPage={10}
          searchPlaceholder={t`Order number or product…`}
          searchField="orderId"
          onRowClick={onOpenOrder ? (row) => !row.current && onOpenOrder(row.orderId || row._id) : undefined}
          renders={[
            {
              id: "orderId",
              title: t`Order`,
              headerClassName: "w-[150px]",
              render: (orderId, row) => (
                <span className="block">
                  <span className="block font-medium text-admin_text">{orderId || String(row._id).slice(-8).toUpperCase()}</span>
                  {row.current && <span className="block text-xs text-admin_primary">{t`this order`}</span>}
                  <span className="block text-xs text-admin_text_secondary">{dateOnly(placedAt(row), locale)}</span>
                </span>
              ),
            },
            {
              id: "cart",
              title: t`Products`,
              render: (cart, row) => (
                <div className="flex flex-col gap-1 min-w-0" data-testid="client-order-lines">
                  {(cart || []).map((line, index) => (
                    <div key={index} className="flex items-center gap-2 min-w-0">
                      <Thumb src={line.thumbnail} name={line.name || line.pid} className="w-[32px] h-[32px] rounded shrink-0" />
                      <span className="truncate text-admin_text">
                        {(line.quantity ?? 1) > 1 ? `${line.quantity} × ` : ""}
                        {describeLine(line, line.name || line.pid)}
                      </span>
                      <span className="ml-auto pl-2 shrink-0 tabular-nums text-admin_text_secondary">{money((line.price ?? 0) * (line.quantity ?? 1), row.currency)}</span>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              id: "amount",
              title: t`Total`,
              headerClassName: "w-[120px] text-right",
              render: (amount, row) => (
                <span className="block text-right tabular-nums text-admin_text">
                  {/* what it came to after its refunds; what was paid, struck through beside it */}
                  {money(row.final?.net ?? amount, row.currency)}
                  {row.refundedAmount > 0 && <span className="block text-xs text-admin_text_secondary line-through">{money(amount, row.currency)}</span>}
                </span>
              ),
            },
            {
              id: "orderStatus",
              title: t`Status`,
              headerClassName: "w-[150px]",
              render: (_, row) => {
                const state = orderState(row)
                const refundState = row.final?.state || "none"
                return (
                  <div className="flex flex-wrap items-center gap-1">
                    <Chip className={state.className}>{t(state.label)}</Chip>
                    {state.label !== "refunded" && refundState === "full" && <Chip className="bg-admin_error/10 text-admin_error">{t`refunded`}</Chip>}
                    {refundState === "partial" && <Chip className="bg-[#b0742a]/10 text-[#b0742a]">{t`partly refunded`}</Chip>}
                    {row.final?.returned === "partial" && <Chip className="bg-[#6b5ca5]/10 text-[#6b5ca5]">{t`partly returned`}</Chip>}
                  </div>
                )
              },
            },
          ]}
          noData={<Empty>{t`No orders found`}</Empty>}
        />
      </div>
    </Modal>
  )
}
